import { InvestigationSuggestion } from "../../lusca-search-api/generated-src";
import { titleForRule } from "./investigationUtils";

/**
 * Plain-language rendering of a TrackStars pattern. The report is written by an agent for TRACK
 * engineers; these helpers turn it into what a person triaging the DLQ reads first: a headline in
 * the registry's own naming, one line on why, one line on the fix, and evidence as counts.
 */

const LAYER_WORDS: Record<string, string> = {
  ingestion: "the ingestion layer",
  accounting: "the accounting layer",
  publishing: "the publishing layer",
};

const REDRIVE_WORDS: Record<string, string> = {
  foil_redrive: "TRACK re-sends the upstream events (FOIL redrive)",
  lusca_redrive: "redrive from Lusca",
};

const capitalise = (text: string) =>
  text ? text[0].toUpperCase() + text.slice(1) : text;

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** "28 Sep" from a TrackStars report id ("trackstars-2026-09-28-…"); "" when it carries no date. */
function runDate(reportId?: string | null): string {
  const m = /trackstars-(\d{4})-(\d{2})-(\d{2})/i.exec(reportId ?? "");
  if (!m) return "";
  const month = MONTHS[Number(m[2]) - 1];
  return month ? `${Number(m[3])} ${month}` : "";
}

/**
 * Display form of a TrackStars pattern id. Every run numbers its patterns from PAT-001, so the
 * short number alone is ambiguous across runs: it is shown with the run's date ("PAT-001 · 28 Sep").
 * Since TrackStars 1da172f ids embed the report id; older ids get the date from `reportId`.
 * Identity (keys, origin, matching) must keep using the full id.
 */
export function shortPatternId(
  patternId?: string | null,
  reportId?: string | null
): string {
  const id = (patternId ?? "").trim();
  const match = /(PAT-\d+)$/i.exec(id);
  if (!match) return id;
  const date = runDate(id) || runDate(reportId);
  const short = match[1].toUpperCase();
  return date ? `${short} · ${date}` : short;
}

/** The report a suggestion came from, when it is not the latest (backend: origin kept across swaps). */
export interface EarlierReport {
  reportId: string;
  generatedAt?: string | null;
  dateRange?: { startDate?: string | null; endDate?: string | null };
  reportUrl?: string;
}

type SuggestionProvenance = InvestigationSuggestion & {
  reportId?: string;
  earlierReport?: EarlierReport | null;
};

/** Set on a pattern an open case was started from that is no longer in the latest report. */
export const earlierReportOf = (
  s: InvestigationSuggestion
): EarlierReport | undefined =>
  (s as SuggestionProvenance).earlierReport ?? undefined;

/** The report a suggestion belongs to (its own when it came from an earlier report). */
export const suggestionReportId = (
  s: InvestigationSuggestion,
  latestReportId?: string
): string | undefined => (s as SuggestionProvenance).reportId || latestReportId;

/** Stable React key: pattern ids repeat across reports. */
export const suggestionKey = (s: InvestigationSuggestion): string =>
  `${(s as SuggestionProvenance).reportId ?? ""}:${s.patternId}`;

/** "28 Sep" for an earlier report's badge (from its id, else its timestamp). */
export const earlierReportLabel = (report: EarlierReport): string => {
  const fromId = runDate(report.reportId);
  if (fromId) return fromId;
  const at = report.generatedAt ? new Date(report.generatedAt) : null;
  return at && !Number.isNaN(at.getTime())
    ? `${at.getUTCDate()} ${MONTHS[at.getUTCMonth()]}`
    : report.reportId;
};

const unownedKnown = (s: InvestigationSuggestion) =>
  typeof (s as InvestigationSuggestion & { unownedCount?: number })
    .unownedCount === "number";

/** Share of a pattern one open case must already hold before the card sends people there. */
export const MOSTLY_HELD_SHARE = 0.8;

type HeldBy = InvestigationSuggestion["ownership"]["investigations"][number] & {
  ownedCount?: number;
};

/**
 * The open case that already holds most of a partly covered pattern, or undefined. Only cases
 * whose rule sits inside the pattern report `ownedCount` (every item they own is a pattern item);
 * `unownedCount` is what the backlog still has, so the share needs no preview.
 */
export const mostlyHeldBy = (
  s: InvestigationSuggestion
): (HeldBy & { share: number; unowned: number }) | undefined => {
  if (s.ownership.state !== "partial") return undefined;
  const unowned =
    (s as InvestigationSuggestion & { unownedCount?: number }).unownedCount ??
    0;
  const cases = s.ownership.investigations as HeldBy[];
  // Nothing left in the backlog: whatever the rules say, the open cases already hold it all.
  if (unowned === 0 && cases.length && unownedKnown(s))
    return { ...cases[0], share: 1, unowned: 0 };
  const top = cases
    .filter((inv) => (inv.ownedCount ?? 0) > 0)
    .sort((a, b) => (b.ownedCount ?? 0) - (a.ownedCount ?? 0))[0];
  if (!top) return undefined;
  const owned = top.ownedCount ?? 0;
  const share = owned / (owned + unowned);
  return share >= MOSTLY_HELD_SHARE ? { ...top, share, unowned } : undefined;
};

/** "IllegalArgumentException on prod-document-builder-intake-dlq (marketplace=US, GL=318)". */
export function suggestionHeadline(s: InvestigationSuggestion): string {
  if (s.derivedRule?.length) return titleForRule(s.derivedRule);
  const text =
    s.description?.trim() || `TrackStars ${shortPatternId(s.patternId)}`;
  return text.length > 120 ? `${text.slice(0, 117).trimEnd()}…` : text;
}

/** The recommended action in words that do not need TrackStars' vocabulary. */
export function describeFix(s: InvestigationSuggestion): string {
  if (s.starter?.fix) return s.starter.fix;
  const kind = s.recommendedAction?.type || "investigate";
  const redrive = s.recommendedAction?.redriveMechanism || "";
  const layer = s.rootCause?.layer || "";
  if (kind === "code_fix") {
    const head = `Code change in ${LAYER_WORDS[layer] ?? "the pipeline"}`;
    return REDRIVE_WORDS[redrive]
      ? `${head}, then ${REDRIVE_WORDS[redrive]}`
      : head;
  }
  if (REDRIVE_WORDS[kind]) return capitalise(REDRIVE_WORDS[kind]);
  if (kind === "monitor")
    return "Monitor — newer event versions may resolve it";
  if (kind === "delete") return "Delete from the DLQ (last resort)";
  // An action type we have no words for: TrackStars' own description beats a generic phrase.
  const said = s.recommendedAction?.description?.trim();
  if (said && kind !== "investigate")
    return said.length > 160 ? `${said.slice(0, 157).trimEnd()}…` : said;
  return "Needs more investigation";
}

/** "high confidence (90%)" */
export function describeConfidence(s: InvestigationSuggestion): string {
  const pct = s.confidencePct ? ` (${s.confidencePct}%)` : "";
  return `${s.confidence || "unknown"} confidence${pct}`;
}

const CR_RE = /\bCR-\d{6,}\b/g;
const COMMIT_RE = /\b[0-9a-f]{7,10}\b/g;

/** "3 commits · 2 CRs · 2 sample transactions" — the evidence, counted rather than quoted. */
export function evidenceSummary(s: InvestigationSuggestion): string {
  const texts = [
    ...(s.rootCause?.evidence ?? []),
    s.recommendedAction?.relatedCommit ?? "",
    s.recommendedAction?.description ?? "",
  ];
  const crs = new Set<string>();
  const commits = new Set<string>();
  texts.forEach((t) => {
    (t.match(CR_RE) ?? []).forEach((m) => crs.add(m));
    (t.match(COMMIT_RE) ?? []).forEach((m) => commits.add(m));
  });
  const parts: string[] = [];
  const count = (n: number, word: string) =>
    `${n} ${word}${n === 1 ? "" : "s"}`;
  if (s.rootCause?.evidence?.length)
    parts.push(count(s.rootCause.evidence.length, "finding"));
  if (commits.size) parts.push(count(commits.size, "commit"));
  if (crs.size) parts.push(count(crs.size, "CR"));
  if (s.samples?.length)
    parts.push(count(s.samples.length, "sample transaction"));
  return parts.length ? parts.join(" · ") : "No evidence recorded";
}

/** Who this pattern's analysis is aimed at: the layer, in words. */
export function layerWord(layer?: string): string {
  return LAYER_WORDS[layer ?? ""] ?? "layer unknown";
}

/**
 * Tokens in agent prose that are code, not words: snake_case identifiers, dotted or
 * parenthesised symbols (`DcdCbeRecordGenerator.build()`, `Amount.value()`), CamelCase class names
 * with a lowercase run, queue names with several hyphens, and `L117-119` style line references.
 * Anchored on word boundaries so ordinary sentences are left alone.
 */
const CODE_TOKEN_RE =
  /(?<![\w.])((?:[A-Za-z_][\w$]*\.)+[A-Za-z_][\w$]*(?:\(\))?|[A-Za-z_][\w$]*\(\)|[a-z0-9]+(?:_[a-z0-9]+){1,}|[a-z0-9]+(?:-[a-z0-9]+){2,}|(?:[A-Z][a-z0-9]+){2,}[A-Za-z0-9]*|L\d+(?:-\d+)?)(?!\.\w)(?![\w(])/g;

/** Product names that look like CamelCase identifiers but are words. */
const NOT_CODE = new Set([
  "TrackStars",
  "TrackStar",
  "CloudWatch",
  "DynamoDB",
  "QuickSight",
  "SharePoint",
  "PySpark",
  "GitHub",
  "OneDrive",
]);

/**
 * Identifiers that are always code, whatever surrounds them: UUIDs and Amazon order IDs
 * (digital `D01-3686401-9398623`, retail `111-1234567-1234567`). Checked before the generic
 * code-token rules so an order ID is never split or dropped as "hyphenated English".
 */
const ID_TOKEN_RE =
  /(?<![\w-])([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}|[A-Z]\d{2}-\d{7}-\d{7}|\d{3}-\d{7}-\d{7})(?![\w-])/g;

const ORDER_ID_RE = /^(?:[A-Z]\d{2}|\d{3})-\d{7}-\d{7}$/;

/** True when the whole string is an Amazon order ID (D01-…, 111-…). */
export const isOrderId = (text: string): boolean => ORDER_ID_RE.test(text);

const UUID_RE =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/** True when the whole string is an order ID or an AUID (UUID) Lusca Search can look up. */
export const isTransactionId = (text: string): boolean =>
  isOrderId(text) || UUID_RE.test(text);

export type ProseSegment = {
  text: string;
  code: boolean;
  /** Set on code segments that are an identifier rather than a symbol. */
  kind?: "uuid" | "orderId";
};

/** Symbol-like tokens (snake_case, dotted, CamelCase…) in a run of prose with no IDs. */
function segmentSymbols(text: string): ProseSegment[] {
  const out: ProseSegment[] = [];
  let last = 0;
  for (const match of text.matchAll(CODE_TOKEN_RE)) {
    const start = match.index ?? 0;
    const token = match[1];
    // Plain capitalised words ("TrackStars", "Lusca") are not code; require a lowercase→uppercase
    // boundary inside CamelCase tokens, or any non-letter marker.
    const isCamel = /^(?:[A-Z][a-z0-9]+){2,}[A-Za-z0-9]*$/.test(token);
    if (isCamel) {
      // A bare CamelCase word ("ChargeFail", "TrackStars") reads as a word; only a class-like name
      // with three or more humps (TvodAccountingTransactionHandler) is code on its own.
      const humps = (token.match(/[A-Z]/g) ?? []).length;
      if (NOT_CODE.has(token) || humps < 3) continue;
    }
    if (/^[a-z0-9]+(?:-[a-z0-9]+){2,}$/.test(token)) {
      // Hyphenated runs are code when they look like a resource name, not hyphenated English
      // ("cash-zero-but-not-full-promo").
      if (
        !/(dlq|queue|intake|publisher|service|lambda|bucket|stack|prod-|gamma-|beta-)/.test(
          token
        )
      )
        continue;
    }
    if (start > last) out.push({ text: text.slice(last, start), code: false });
    out.push({ text: token, code: true });
    last = start + token.length;
  }
  if (last < text.length) out.push({ text: text.slice(last), code: false });
  return out;
}

/** Split prose into plain and code segments for inline rendering. */
export function segmentCode(text: string): ProseSegment[] {
  const out: ProseSegment[] = [];
  let last = 0;
  Array.from(text.matchAll(ID_TOKEN_RE)).forEach((match) => {
    const start = match.index ?? 0;
    const token = match[1];
    if (start > last) out.push(...segmentSymbols(text.slice(last, start)));
    out.push({
      text: token,
      code: true,
      kind: isOrderId(token) ? "orderId" : "uuid",
    });
    last = start + token.length;
  });
  if (last < text.length) out.push(...segmentSymbols(text.slice(last)));
  return out;
}
